import React from "react";
import moment from "moment";
import { currencySymbol } from "shared/CurrencySymbol";
import { BookingStatus, getAmountDue, firstName } from "../bookingStatus";

interface Props {
  booking: any;
  status: BookingStatus;
  isHost: boolean;
  /** Opens the existing payment window (guest only). */
  onPay: () => void;
}

const Row = ({ label, value, discount }: { label: React.ReactNode; value: string; discount?: boolean }) => (
  <div className="flex justify-between gap-3 text-[13px]">
    <span className="truncate text-neutral-500 dark:text-neutral-400">{label}</span>
    <span className={`whitespace-nowrap ${discount ? "text-green-700 dark:text-green-400" : "text-neutral-900 dark:text-neutral-100"}`}>{value}</span>
  </div>
);

const shortDate = (v: any) => {
  if (!v) return "";
  const m = moment.utc(v, [moment.ISO_8601, "MMMM DD, YYYY"], true);
  return m.isValid() ? m.format("MMM D, YYYY") : String(v);
};

/**
 * Invoice shown inside the Messages thread, under the system notice.
 * Numbers come from the live booking, so the status always matches the page.
 */
const InvoiceMessageCard: React.FC<Props> = ({ booking: b, status, isHost, onPay }) => {
  // The invoice was withdrawn (or never existed): nothing to show.
  if (status === "booking_request") return null;

  const money = (n: number) => `${currencySymbol()}${(Number.isFinite(n) ? n : 0).toFixed(2)}`;
  const invoiceNo = b?.invoiceNo ?? b?.invoice?.id;
  const rent = Number(b?.item_total) || 0;
  const fee = Number(b?.service_tax) || 0;
  const extraGuests = Number(b?.extra_guest_amount) || 0;
  const tax = Number(b?.tax_total) || 0;
  const expenses: any[] = b?.invoice?.expenses || [];
  const discounts: any[] = b?.invoice?.discounts || [];
  const total = getAmountDue(b);

  const paid = status === "payment_received" || status === "confirmed";
  const canceled = status === "canceled";
  const pill = canceled
    ? { t: "Canceled", c: "bg-neutral-200 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200" }
    : paid
    ? { t: "Paid", c: "bg-green-100 text-green-900 dark:bg-green-900/30 dark:text-green-300" }
    : { t: "Payment due", c: "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-300" };

  const who = isHost ? "Invoice for " + (firstName(b?.user?.first_name ?? b?.request_by) || "the guest") : "Invoice from " + (firstName(b?.property_user?.first_name ?? b?.property_user?.name) || "your host");
  const meta = [b?.booked_room, [shortDate(b?.check_in_date), shortDate(b?.check_out_date)].filter(Boolean).join(" to ")].filter(Boolean).join(" · ");

  return (
    <div className="flex justify-center py-1">
      <div className="w-full max-w-md overflow-hidden rounded-2xl border border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-900">
        <div className="flex items-center justify-between gap-3 border-b border-neutral-100 px-4 py-3 dark:border-neutral-800">
          <div className="min-w-0">
            <div className="text-xs text-neutral-500 dark:text-neutral-400">{who}</div>
            <div className="text-base font-semibold text-neutral-900 dark:text-white">
              {invoiceNo != null && invoiceNo !== "" ? `Invoice #${invoiceNo}` : "Invoice"}
            </div>
          </div>
          <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${pill.c}`}>{pill.t}</span>
        </div>

        <div className="space-y-1.5 px-4 py-3">
          {meta && <p className="mb-2 text-xs text-neutral-500 dark:text-neutral-400">{meta}</p>}
          {rent > 0 && <Row label="First month's rent" value={money(rent)} />}
          {fee > 0 && <Row label={isHost ? "Service fee (Nomadroof)" : "Service fee"} value={money(fee)} />}
          {extraGuests > 0 && <Row label="Extra guests" value={money(extraGuests)} />}
          {tax > 0 && <Row label="Tax" value={money(tax)} />}
          {expenses.map((e, i) => (
            <Row key={`e${i}`} label={<span className="capitalize">{e.category}</span>} value={money(Number(e.amount))} />
          ))}
          {discounts.map((d, i) => (
            <Row key={`d${i}`} label={<span className="capitalize">{d.description}</span>} value={`− ${money(Number(d.amount))}`} discount />
          ))}
          <div className="flex items-baseline justify-between border-t border-neutral-100 pt-2.5 dark:border-neutral-800">
            <span className="text-sm font-semibold text-neutral-900 dark:text-white">{paid ? "Total paid" : "Total due"}</span>
            <span className="text-xl font-semibold text-[#C2410C] dark:text-[#FF8A6E]">{money(total)}</span>
          </div>
        </div>

        {!isHost && status === "invoice_sent" && (
          <div className="px-4 pb-4">
            <button
              type="button"
              onClick={onPay}
              className="min-h-[48px] w-full rounded-xl bg-[#C2410C] px-4 text-sm font-semibold text-white hover:bg-[#9A3412] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C2410C] focus-visible:ring-offset-2"
            >
              Check &amp; pay invoice
            </button>
          </div>
        )}
        {isHost && status === "invoice_sent" && (
          <p className="px-4 pb-4 text-xs text-neutral-500 dark:text-neutral-400">Waiting for the guest to pay.</p>
        )}
      </div>
    </div>
  );
};

export default InvoiceMessageCard;
